import { useState } from "react";
import Layout from "@/layouts/layout";
import PageHeading from "@/components/layout/PageHeading";
import Panel from "@/components/ui/Panel";
import Button, { ButtonLink } from "@/components/ui/Button";
import Badge from "@/components/ui/Badge";
import EmptyState from "@/components/ui/EmptyState";
import { Field, Input, Textarea } from "@/components/ui/Field";
import EventStatusBadge from "@/components/events/EventStatusBadge";
import { useForm, router, usePage } from "@inertiajs/react";
import type { SharedProps } from "@/types";
import type { SatelliteEvent, EventRsvp } from "@/interfaces/satellite_event";
import { Select } from "@/components/ui/Field";
import EventTeam, { type TeamMember } from "@/components/events/EventTeam";
import EventThread, {
  type ThreadMessage,
} from "@/components/events/EventThread";
import EventChecklist, {
  type ChecklistStage,
} from "@/components/events/EventChecklist";
import EventBudget, {
  type EventBudgetSummary,
} from "@/components/events/EventBudget";
import HackatimeConnect from "@/components/projects/HackatimeConnect";
import CheckInCalls from "@/components/events/CheckInCalls";
import type { CheckInCall } from "@/interfaces/check_in_call";
import type { Submission } from "@/interfaces/submission";
import type { TimeZoneOption } from "@/utils/guessTimeZone";
import type { ProgramWindow } from "@/interfaces/program";
import { formatWindow } from "@/utils/programWindow";
import EventReschedule from "@/components/events/EventReschedule";
import RecapPhotos from "@/components/events/RecapPhotos";
import { ageFrom, isMinorOn } from "@/utils/age";

// The link an organizer actually shares: on a poster, in an Instagram bio, read
// out loud. "/events/32" is none of those things, so they get to name it.
function ShareLink({
  event,
  canEdit,
}: {
  event: SatelliteEvent;
  canEdit: boolean;
}) {
  const [slug, setSlug] = useState(event.slug ?? "");
  const [copied, setCopied] = useState(false);
  const dirty = slug !== (event.slug ?? "");

  // The clipboard API needs a secure context and permission, and silently
  // rejects in enough places that a copy button with no fallback is a button
  // that sometimes does nothing.
  async function copy() {
    try {
      await navigator.clipboard.writeText(event.share_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt("Copy your event link:", event.share_url);
    }
  }

  return (
    <Panel title="share-link.txt">
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <code className="border-line bg-surface-plain text-body text-ink font-literal min-w-0 flex-1 truncate border-2 px-3 py-2">
            {event.share_url}
          </code>
          <Button onClick={copy}>{copied ? "Copied!" : "Copy link"}</Button>
        </div>

        {canEdit && (
          <form
            className="flex flex-col gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              router.patch(
                `/organize/${event.slug ?? event.id}`,
                { slug },
                { preserveScroll: true },
              );
            }}
          >
            <Field
              label="Customize it"
              hint="Lowercase letters, numbers and hyphens. The old link keeps working."
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-caption text-ink-soft font-literal">
                  /events/
                </span>
                <Input
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  placeholder="your-city"
                  maxLength={60}
                  className="max-w-xs"
                  aria-label="Event link"
                />
                <Button type="submit" size="sm" disabled={!dirty}>
                  Save link
                </Button>
              </div>
            </Field>
          </form>
        )}
      </div>
    </Panel>
  );
}

// The hand-in link, ready to paste. Same clipboard fallback as ShareLink: the
// API needs a secure context and permission and rejects in enough places that a
// copy button without one is a button that sometimes does nothing.
function HandInLink({ event }: { event: SatelliteEvent }) {
  const [copied, setCopied] = useState(false);
  const url = `${event.share_url.replace(/\/$/, "")}/submit`;

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt("Copy your hand-in link:", url);
    }
  }

  return (
    <div className="border-line bg-surface-plain mb-4 flex flex-col gap-2 border-2 p-3">
      <p className="text-caption text-ink">
        Send this to everyone on your roster. Every site that lands before you
        start is budget for your city.
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <code className="border-line bg-surface text-caption text-ink font-literal min-w-0 flex-1 truncate border-2 px-3 py-2">
          {url}
        </code>
        <Button size="sm" onClick={copy}>
          {copied ? "Copied!" : "Copy link"}
        </Button>
      </div>
    </div>
  );
}

function AttendeeRow({
  rsvp,
  eventId,
  checkInOpen,
  // A site-funded city earns nothing per signup, so the two badges that exist
  // to explain a grant number have nothing to explain here.
  fundedBySites,
}: {
  rsvp: EventRsvp;
  eventId: number;
  checkInOpen: boolean;
  fundedBySites: boolean;
}) {
  const checkedIn = rsvp.aasm_state === "checked_in";

  return (
    <div className="border-line/20 flex items-center gap-3 border-b py-2.5 last:border-b-0">
      <div className="border-line bg-surface-plain h-8 w-8 shrink-0 overflow-hidden rounded-full border">
        {rsvp.user?.avatar ? (
          <img
            src={rsvp.user.avatar}
            alt=""
            className="h-full w-full object-cover"
          />
        ) : (
          <span className="font-display text-caption text-ink flex h-full w-full items-center justify-center font-bold">
            {rsvp.name?.charAt(0)?.toUpperCase() ?? "?"}
          </span>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <p className="text-body text-ink truncate font-bold">
          {rsvp.name ?? "Someone"}
        </p>
        <p className="text-caption text-ink-soft truncate">{rsvp.email}</p>
      </div>

      {rsvp.funding_excluded && !fundedBySites && (
        <Badge
          tone="neutral"
          title={rsvp.funding_exclusion_reason ?? undefined}
        >
          Not funded
        </Badge>
      )}
      {checkedIn && <Badge tone="success">Checked in</Badge>}
      {!checkedIn && !rsvp.confirmed && (
        <Badge
          // Neutral rather than a warning where no money hangs on it: the
          // colour is what makes an organizer go and chase somebody.
          tone={fundedBySites ? "neutral" : "warning"}
          title={
            fundedBySites
              ? "They haven't clicked the link in their confirmation email yet. Only they can do that, and it doesn't change what your city is funded for."
              : "They haven't clicked the link in their confirmation email yet. Only they can do that - it isn't something for you to fix."
          }
        >
          Email not confirmed
        </Badge>
      )}

      <Button
        size="sm"
        variant={checkedIn ? "secondary" : "primary"}
        // Undo stays available: it only ever takes a check-in away.
        disabled={!checkedIn && !checkInOpen}
        title={
          !checkedIn && !checkInOpen
            ? "Check-in opens on the day of your event."
            : undefined
        }
        onClick={() =>
          checkedIn
            ? router.delete(`/organize/${eventId}/rsvps/${rsvp.id}/check_in`, {
                preserveScroll: true,
              })
            : router.post(
                `/organize/${eventId}/rsvps/${rsvp.id}/check_in`,
                {},
                { preserveScroll: true },
              )
        }
      >
        {checkedIn ? "Undo" : "Check in"}
      </Button>

      {/* Duplicates, typo'd addresses, and people who told the organizer in
          person they aren't coming. Confirming first because it frees their spot
          and takes them off what the event is funded for. */}
      <Button
        size="sm"
        variant="secondary"
        onClick={() => {
          if (
            !window.confirm(
              `Remove ${rsvp.name ?? "this signup"} from the list? This frees their spot.`,
            )
          )
            return;
          router.delete(`/organize/${eventId}/rsvps/${rsvp.id}`, {
            preserveScroll: true,
          });
        }}
      >
        Remove
      </Button>
    </div>
  );
}

// One hand-in on the organizer's list. The public gallery shows a first name
// and the link; this shows who it actually is, and the take-down.
function SubmissionRow({
  submission,
  eventId,
}: {
  submission: Submission;
  eventId: number;
}) {
  return (
    <div className="border-line flex flex-wrap items-center gap-3 border-2 p-3">
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-display text-body text-ink font-bold">
            {submission.title}
          </span>
          {submission.removed && <Badge tone="danger">Taken down</Badge>}
          {/* Handing in needs no RSVP, so this is somebody the organizer has no
              other record of: they won't be on the roster above and check-in
              won't find them. Worth knowing before the day, not at the door. */}
          {submission.on_the_roster === false && (
            <Badge
              tone="info"
              title="They handed in a site but never RSVP'd, so they aren't on your roster and check-in won't find them."
            >
              Not signed up
            </Badge>
          )}
          {/* Where it stands in the queue. Shown because the attendee will ask
              the organizer before they think to check their email, and because
              on an international event a verdict is what the city gets paid
              for. The reviewer's note is not here: it's addressed to the
              author, not to the person running the room. */}
          {!submission.removed && submission.review_state === "pending" && (
            <Badge tone="info">In review</Badge>
          )}
          {!submission.removed && submission.review_state === "rejected" && (
            <Badge tone="warning">Sent back</Badge>
          )}
          {submission.counts_for_prefunding && (
            <Badge
              tone="success"
              title="Handed in before the start and approved, so it counts towards this event's pre-funding"
            >
              Counts
            </Badge>
          )}
          {!submission.removed &&
            submission.review_state === "approved" &&
            submission.before_event_start === false && (
              <Badge
                tone="neutral"
                title="Approved and on the wall, but handed in after the event started, so it isn't part of what the event was pre-funded for"
              >
                After the start
              </Badge>
            )}
        </div>
        <a
          href={submission.live_url}
          target="_blank"
          rel="noopener noreferrer nofollow ugc"
          className="text-link text-caption break-all underline"
        >
          {submission.live_url}
        </a>
        <span className="text-caption text-ink-soft">
          {submission.attendee_name}
          {submission.attendee_email ? ` - ${submission.attendee_email}` : ""}
        </span>
      </div>
      <Button
        variant={submission.removed ? "secondary" : "danger"}
        size="sm"
        onClick={() => {
          if (submission.removed) {
            router.post(
              `/organize/${eventId}/submissions/${submission.id}/restore`,
              {},
              { preserveScroll: true },
            );
          } else {
            router.delete(`/organize/${eventId}/submissions/${submission.id}`, {
              preserveScroll: true,
            });
          }
        }}
      >
        {submission.removed ? "Put it back" : "Take down"}
      </Button>
    </div>
  );
}

export default function OrganizeShow({
  event,
  rsvps,
  submissions,
  can_edit,
  can_submit,
  time_zones,
  stages,
  team_members,
  messages,
  is_hq,
  program_window,
  october_window,
  original_window,
  budget,
  check_in_calls,
  venue_types,
  is_poc,
  organizer_details,
  recap_photos,
  recap_photos_link,
  can_upload_photos,
  recap_photo_limit,
}: {
  event: SatelliteEvent;
  rsvps: EventRsvp[];
  submissions: Submission[];
  can_edit: boolean;
  can_submit: boolean;
  time_zones: TimeZoneOption[];
  stages: ChecklistStage[];
  messages: ThreadMessage[];
  is_hq: boolean;
  team_members: TeamMember[];
  program_window: ProgramWindow;
  october_window: ProgramWindow;
  original_window: ProgramWindow;
  budget: EventBudgetSummary;
  check_in_calls: CheckInCall[];
  venue_types: { value: string; label: string }[];
  // A co-organizer edits the event like the point of contact does, but the
  // point of contact's own details are theirs to fill in.
  is_poc: boolean;
  organizer_details: {
    birthday: string | null;
    parent_name: string | null;
    parent_email: string | null;
    minor: boolean;
    on_file: boolean;
  };
  recap_photos: string[];
  recap_photos_link: string | null;
  can_upload_photos: boolean;
  recap_photo_limit: number;
}) {
  const { data, setData, patch, processing, errors } = useForm({
    name: event.name ?? "",
    city: event.city ?? "",
    region: event.region ?? "",
    country: event.country ?? "",
    venue_name: event.venue_name ?? "",
    venue_address: event.venue_address ?? "",
    venue_address_line_2: event.venue_address_line_2 ?? "",
    venue_city: event.venue_city ?? "",
    venue_region: event.venue_region ?? "",
    venue_postal_code: event.venue_postal_code ?? "",
    starts_at: event.starts_at_local ?? "",
    ends_at: event.ends_at_local ?? "",
    capacity: event.capacity ?? "",
    description: event.description ?? "",
    expected_attendees: event.expected_attendees ?? "",
    venue_type: event.venue_type ?? "",
    organizer_slack_id: event.organizer_slack_id ?? "",
    birthday: organizer_details.birthday ?? "",
    parent_name: organizer_details.parent_name ?? "",
    parent_email: organizer_details.parent_email ?? "",
    pitch: event.pitch ?? "",
    venue_plan: event.venue_plan ?? "",
    experience: event.experience ?? "",
    time_zone: event.time_zone ?? "UTC",
  });

  const approved = event.aasm_state === "approved";
  const notSelected = event.aasm_state === "not_selected";
  const program = usePage<SharedProps>().props.program;
  const rsvpsOpen = program.rsvps_open;
  // Hack Club Auth gives us a birthdate at sign-in, so the date field only
  // shows for the accounts it returned nothing for.
  const knownBirthday = organizer_details.on_file;
  // Recomputed from the field rather than read off the prop, so the guardian
  // fields appear as soon as they type a date rather than after a save.
  const isMinor = isMinorOn(data.birthday);
  // These describe the point of contact, not the event, so a co-organizer never
  // sees them -- the server won't accept them from anyone else either.
  const askBirthday = is_poc && !knownBirthday;
  const askGuardian = is_poc && isMinor;

  // The window every Boba Bash has to land in. datetime-local wants a full
  // local timestamp, so the bounds are the first minute and the last minute of
  // the span. Weekends are a preference, not a rule, so they're a hint.
  const windowMin = program_window.starts_on
    ? `${program_window.starts_on}T00:00`
    : undefined;
  const windowMax = program_window.ends_on
    ? `${program_window.ends_on}T23:59`
    : undefined;
  const windowHint = program_window.starts_on
    ? `Boba Bash runs ${formatWindow(program_window)}. Weekends work best.`
    : "This event's local time.";

  return (
    <Layout>
      <div className="flex flex-col gap-8">
        <PageHeading
          title={event.name}
          subtitle={event.location_label}
          actions={
            <div className="flex flex-wrap items-center gap-3">
              {approved && (program.organizer_boards_open || is_hq) && (
                <ButtonLink
                  href={`/organize/${event.id}/page`}
                  variant="secondary"
                >
                  Edit event page
                </ButtonLink>
              )}
              <EventStatusBadge state={event.aasm_state} />
            </div>
          }
        />

        {!program.organizer_boards_open && (
          <Panel title="season-closed.txt" tone="alt">
            <p className="text-body text-ink-muted">
              {program.name} is over for this year. Thank you for running it!
              Your board stays here as the record of your event, but it's
              read-only now. If something on it is wrong, message us at{" "}
              {program.support_email || "HQ"}.
            </p>
          </Panel>
        )}

        {can_upload_photos && event.past && (
          <RecapPhotos
            eventId={event.id}
            photos={recap_photos}
            limit={recap_photo_limit}
            link={recap_photos_link}
          />
        )}

        {/* Above everything else on purpose: an organizer whose date moved has
            one thing to do, and it isn't reading their checklist. */}
        <EventReschedule
          event={event}
          canEdit={can_edit}
          octoberWindow={october_window}
          originalWindow={original_window}
        />

        {event.aasm_state === "rejected" && event.review_notes && (
          <Panel title="review-notes.txt" tone="alt">
            <p className="text-body text-ink-muted">{event.review_notes}</p>
          </Panel>
        )}

        {/* Nothing here is fixable, so this offers the two ways still in
            instead of a form and a resubmit button. */}
        {notSelected && (
          <Panel title="about-your-application.txt" tone="alt">
            <div className="flex flex-col gap-3">
              <p className="text-body text-ink-muted">
                More than one person applied to run this one, and we asked
                someone else to be the point of contact. That's not a comment on
                your application - there can only be one person holding the
                venue and the boba order.
              </p>

              {event.review_notes && (
                <p className="text-body text-ink-muted">{event.review_notes}</p>
              )}

              <div className="flex flex-wrap gap-3">
                {event.offered_event && (
                  <ButtonLink href={`/events/${event.offered_event.id}`}>
                    See {event.offered_event.name}
                  </ButtonLink>
                )}
                <ButtonLink href="/organize/new" variant="secondary">
                  Apply for another city
                </ButtonLink>
              </div>

              {event.offered_event && (
                <p className="text-caption text-ink-soft">
                  Want to help run it? Message the organizer and they can add
                  you to their team.
                </p>
              )}
            </div>
          </Panel>
        )}

        {approved && <ShareLink event={event} canEdit={can_edit} />}

        {approved && (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
            <Panel title="rsvps.count">
              <p className="font-display text-display text-carrot-orange font-bold">
                {event.rsvp_count ?? 0}
              </p>
              <p className="text-caption text-ink-muted">going</p>
            </Panel>
            <Panel title="checked-in.count">
              <p className="font-display text-display text-leaf font-bold">
                {event.checked_in_count ?? 0}
              </p>
              <p className="text-caption text-ink-muted">checked in</p>
            </Panel>
            <Panel title="when.txt">
              <p className="text-body text-ink font-bold">
                {event.starts_at_label ?? "Date TBD"}
                {event.time_zone_label ? ` ${event.time_zone_label}` : ""}
              </p>
              {event.venue_name && (
                <p className="text-caption text-ink-muted mt-1">
                  {event.venue_name}
                </p>
              )}
            </Panel>
          </div>
        )}

        {/* Approval opens this channel automatically, so the link is the only
            thing an organizer needs - not an explanation of where it went. */}
        {approved && event.slack_channel_url && (
          <Panel title="slack.channel">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-body text-ink font-bold">
                  #{event.slack_channel_name}
                </p>
                <p className="text-caption text-ink-muted">
                  You, your team and HQ are already in it. Ask questions there
                  rather than in a DM - the answer usually helps another city.
                </p>
              </div>
              <ButtonLink href={event.slack_channel_url}>
                Open in Slack
              </ButtonLink>
            </div>
          </Panel>
        )}

        {approved && <CheckInCalls calls={check_in_calls} />}

        {approved && (
          <EventBudget budget={budget} eventId={event.slug ?? event.id} />
        )}

        {/* The hours half of the budget needs a linked Hackatime account, and
            an organizer who applied for Boba Bash never sees forced onboarding
            where that link used to be the only one. */}
        {approved && program.projects_enabled && <HackatimeConnect compact />}

        {approved && (
          <EventTeam
            members={team_members}
            eventId={event.id}
            canRemove={is_poc || is_hq}
          />
        )}

        {approved && (
          <EventThread
            eventId={event.id}
            initialMessages={messages}
            isHq={is_hq}
          />
        )}

        {approved && <EventChecklist stages={stages} eventId={event.id} />}

        {approved && (
          <Panel title={`attendees.csv - ${rsvps.length}`}>
            {/* An organizer whose page is live but getting no sign-ups should
                not have to guess why: say it's the platform-wide switch, not
                them. */}
            {rsvps.length === 0 ? (
              <EmptyState
                title={rsvpsOpen ? "No RSVPs yet" : "Sign-ups haven't opened"}
                description={
                  rsvpsOpen
                    ? "Share your event page and they'll show up here."
                    : "Your event page is live and shareable now - people can see it, but nobody can RSVP just yet. We'll open sign-ups soon and attendees will land here."
                }
              />
            ) : (
              <div className="flex flex-col">
                {!rsvpsOpen && (
                  <p className="text-caption text-ink-muted pb-4">
                    Sign-ups are closed platform-wide right now - we'll open
                    them soon.
                  </p>
                )}
                {/* The two words got read as the same thing, and checking
                    somebody in counts as confirming them when the grant is
                    worked out - so an organizer tidying up "Unconfirmed" rows
                    was quietly making unconfirmed signups fundable. */}
                <p className="text-caption text-ink-muted pb-4">
                  <span className="text-ink font-bold">Confirmed</span> is the
                  attendee clicking the link in their email - only they can do
                  it, and there's nothing for you to fix here.{" "}
                  <span className="text-ink font-bold">Check in</span> is you,
                  on the day, marking who actually turned up.
                  {/* The warning above it is about the signup grant, which a
                      site-funded city isn't on. Saying so here stops an
                      organizer chasing confirmations that aren't worth
                      anything to them. */}
                  {budget.funding_model === "sites" && (
                    <>
                      {" "}
                      Neither one is money for your city on its own - your
                      budget comes from the sites people hand in. But handing in
                      needs a code emailed to an address on this list, so
                      everybody who builds something has to be on it first.
                    </>
                  )}
                  {!event.check_in_open && (
                    <>
                      {" "}
                      It opens
                      {event.check_in_opens_label
                        ? ` on ${event.check_in_opens_label}`
                        : " on the day of your event"}
                      .
                    </>
                  )}
                </p>
                {rsvps.map((rsvp) => (
                  <AttendeeRow
                    key={rsvp.id}
                    rsvp={rsvp}
                    eventId={event.id}
                    checkInOpen={event.check_in_open ?? false}
                    fundedBySites={budget.funding_model === "sites"}
                  />
                ))}
              </div>
            )}
          </Panel>
        )}

        {approved && (
          <Panel title={`handed-in.csv - ${submissions.length}`}>
            {/* The link an organizer pastes into their group chat. The event
                page is a poster; this is the one thing they need people to
                actually do, and asking them to build the URL by hand is how it
                never gets shared. */}
            {budget.funding_model === "sites" && event.submissions_open && (
              <HandInLink event={event} />
            )}
            {submissions.length === 0 ? (
              <EmptyState
                title="Nothing handed in yet"
                description={
                  !event.submissions_open
                    ? "Hand-ins aren't open yet. HQ turns them on for everyone at once."
                    : budget.funding_model === "sites"
                      ? "This is where your budget comes from. Every site handed in before your start time is worth money to your city once a reviewer approves it, so the push to get people building happens before the day, not on it."
                      : "Attendees hand in the site they built from the event page. It shows up here as they do."
                }
              />
            ) : (
              <div className="flex flex-col gap-3">
                {/* The one thing a site-funded organizer has to understand:
                    the deadline is their start time, not the end of the day.
                    Said where they watch hand-ins arrive, because that is when
                    it is still possible to act on it. */}
                {budget.funding_model === "sites" && (
                  <p className="text-caption text-ink-muted">
                    Sites handed in{" "}
                    <span className="text-ink font-bold">
                      before your start time
                    </span>{" "}
                    are what your city is funded for. Anything after it still
                    counts as work and still goes on the wall, it just isn't
                    part of the grant.
                  </p>
                )}
                {/* Counted at the panel rather than left to the badges, because
                    these are extra people to expect and an organizer reading
                    row by row will miss how many there are. */}
                {submissions.some((s) => s.on_the_roster === false) && (
                  <p className="text-caption text-ink-muted">
                    <span className="text-ink font-bold">
                      {
                        submissions.filter((s) => s.on_the_roster === false)
                          .length
                      }{" "}
                      of these handed in without signing up
                    </span>{" "}
                    - they aren't on your roster above and check-in won't find
                    them. Still real work, and still counts, but they're people
                    to expect who aren't on your list.
                  </p>
                )}
                <p className="text-caption text-ink-soft">
                  The gallery is public. Take anything down that shouldn't be on
                  it - it stays on this list so you can put it back.
                  {budget.funding_model === "sites" &&
                    " Sites go up once a reviewer has approved them, which happens away from your event - it isn't something for you to do."}
                </p>
                {submissions.map((submission) => (
                  <SubmissionRow
                    key={submission.id}
                    submission={submission}
                    eventId={event.id}
                  />
                ))}
              </div>
            )}
          </Panel>
        )}

        {!approved && !notSelected && (
          <Panel title="your-application.txt" className="max-w-2xl">
            <div className="flex flex-col gap-3">
              <p className="text-body text-ink-muted">
                {event.aasm_state === "submitted"
                  ? "Your application is in. We'll be in touch once we've picked the point of contact for your city."
                  : "Finish your application and submit it when you're ready."}
              </p>
              <p className="text-caption text-ink-soft">
                Venue, date and your team all get sorted after you're picked -
                nothing to do there yet.
              </p>
            </div>
          </Panel>
        )}

        {!notSelected && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              patch(`/organize/${event.id}`);
            }}
          >
            {/* Venue, date and capacity only exist once someone's been picked. */}
            <Panel
              title={approved ? "event-details.form" : "application.form"}
              className="max-w-2xl"
            >
              <div className="flex flex-col gap-5">
                <Field label="Event name" error={errors.name}>
                  <Input
                    value={data.name}
                    onChange={(e) => setData("name", e.target.value)}
                    disabled={!can_edit}
                  />
                </Field>

                <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
                  <Field label="City" error={errors.city}>
                    <Input
                      value={data.city}
                      onChange={(e) => setData("city", e.target.value)}
                      disabled={!can_edit}
                    />
                  </Field>
                  <Field label="Region" error={errors.region}>
                    <Input
                      value={data.region}
                      onChange={(e) => setData("region", e.target.value)}
                      disabled={!can_edit}
                    />
                  </Field>
                  <Field label="Country" error={errors.country}>
                    <Input
                      value={data.country}
                      onChange={(e) => setData("country", e.target.value)}
                      disabled={!can_edit}
                    />
                  </Field>
                </div>

                {/* The answers HQ reads. Editable until selection, so someone
                  sent back with notes can actually act on them. */}
                {!approved && (
                  <>
                    <Field
                      label="Roughly how many people?"
                      hint="A guess is completely fine."
                      error={errors.expected_attendees}
                    >
                      <Input
                        type="number"
                        min={1}
                        value={data.expected_attendees}
                        onChange={(e) =>
                          setData("expected_attendees", e.target.value)
                        }
                        disabled={!can_edit}
                      />
                    </Field>

                    <Field
                      label="Slack member ID"
                      hint="In Slack, open your profile, click the ⋮ and choose “Copy member ID”."
                      error={errors.organizer_slack_id}
                    >
                      <Input
                        value={data.organizer_slack_id}
                        onChange={(e) =>
                          setData("organizer_slack_id", e.target.value)
                        }
                        placeholder="U01ABC2DEFG"
                        disabled={!can_edit}
                      />
                    </Field>

                    {askBirthday && (
                      <Field
                        label="Your date of birth"
                        hint="HCB needs it to set up your event's money."
                        error={errors.birthday}
                      >
                        <Input
                          type="date"
                          value={data.birthday}
                          onChange={(e) => setData("birthday", e.target.value)}
                          disabled={!can_edit}
                        />
                      </Field>
                    )}

                    {askGuardian && (
                      <>
                        <Field
                          label="Parent or guardian's name"
                          hint={
                            knownBirthday
                              ? `Your Hack Club account puts you at ${ageFrom(data.birthday)}, so a guardian co-signs the HCB agreement. We email them the form.`
                              : "They co-sign the HCB agreement - we email them the form."
                          }
                          error={errors.parent_name}
                        >
                          <Input
                            value={data.parent_name}
                            onChange={(e) =>
                              setData("parent_name", e.target.value)
                            }
                            disabled={!can_edit}
                          />
                        </Field>

                        <Field
                          label="Parent or guardian's email"
                          error={errors.parent_email}
                        >
                          <Input
                            type="email"
                            value={data.parent_email}
                            onChange={(e) =>
                              setData("parent_email", e.target.value)
                            }
                            disabled={!can_edit}
                          />
                        </Field>
                      </>
                    )}

                    <Field
                      label="Why do you want to run one?"
                      hint="A few sentences about you and what you're picturing."
                      error={errors.pitch}
                    >
                      <Textarea
                        rows={4}
                        value={data.pitch}
                        onChange={(e) => setData("pitch", e.target.value)}
                        disabled={!can_edit}
                      />
                    </Field>

                    <Field
                      label="Have you run something like this before?"
                      hint="Optional. It's completely fine if this is your first one."
                      error={errors.experience}
                    >
                      <Textarea
                        rows={3}
                        value={data.experience}
                        onChange={(e) => setData("experience", e.target.value)}
                        disabled={!can_edit}
                      />
                    </Field>
                  </>
                )}

                {approved && (
                  <>
                    <Field
                      label="Time zone"
                      hint="Times below are this event's local time."
                      error={errors.time_zone}
                    >
                      <Select
                        value={data.time_zone}
                        onChange={(e) => setData("time_zone", e.target.value)}
                        disabled={!can_edit}
                      >
                        {time_zones.map((tz) => (
                          <option key={tz.name} value={tz.name}>
                            {tz.label}
                          </option>
                        ))}
                      </Select>
                    </Field>

                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                      <Field
                        label="Starts at"
                        hint={windowHint}
                        error={errors.starts_at}
                      >
                        <Input
                          type="datetime-local"
                          value={data.starts_at}
                          onChange={(e) => setData("starts_at", e.target.value)}
                          min={windowMin}
                          max={windowMax}
                          disabled={!can_edit}
                        />
                      </Field>
                      <Field label="Ends at" error={errors.ends_at}>
                        <Input
                          type="datetime-local"
                          value={data.ends_at}
                          onChange={(e) => setData("ends_at", e.target.value)}
                          min={windowMin}
                          max={windowMax}
                          disabled={!can_edit}
                        />
                      </Field>
                    </div>

                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                      <Field
                        label="What kind of place?"
                        error={errors.venue_type}
                      >
                        <Select
                          value={data.venue_type}
                          onChange={(e) =>
                            setData("venue_type", e.target.value)
                          }
                          disabled={!can_edit}
                        >
                          <option value="">Pick one…</option>
                          {venue_types.map((v) => (
                            <option key={v.value} value={v.value}>
                              {v.label}
                            </option>
                          ))}
                        </Select>
                      </Field>

                      <Field label="Venue name" error={errors.venue_name}>
                        <Input
                          value={data.venue_name}
                          onChange={(e) =>
                            setData("venue_name", e.target.value)
                          }
                          disabled={!can_edit}
                        />
                      </Field>
                      <Field
                        label="Capacity"
                        hint="Leave blank for no limit."
                        error={errors.capacity}
                      >
                        <Input
                          type="number"
                          min={1}
                          value={data.capacity}
                          onChange={(e) => setData("capacity", e.target.value)}
                          disabled={!can_edit}
                        />
                      </Field>
                    </div>

                    <Field
                      label="Venue address"
                      hint="Only shown to you and to HQ."
                      error={errors.venue_address}
                    >
                      <Input
                        value={data.venue_address}
                        onChange={(e) =>
                          setData("venue_address", e.target.value)
                        }
                        placeholder="123 Main St"
                        disabled={!can_edit}
                      />
                    </Field>

                    <Field
                      label="Address line 2"
                      hint="Room or floor, if there is one."
                      error={errors.venue_address_line_2}
                    >
                      <Input
                        value={data.venue_address_line_2}
                        onChange={(e) =>
                          setData("venue_address_line_2", e.target.value)
                        }
                        placeholder="Meeting room B"
                        disabled={!can_edit}
                      />
                    </Field>

                    {/* A bank account is opened against this address, so it's
                        split rather than one free-text line. */}
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
                      <Field label="Venue city" error={errors.venue_city}>
                        <Input
                          value={data.venue_city}
                          onChange={(e) =>
                            setData("venue_city", e.target.value)
                          }
                          placeholder={event.city ?? "San Jose"}
                          disabled={!can_edit}
                        />
                      </Field>

                      <Field label="State / region" error={errors.venue_region}>
                        <Input
                          value={data.venue_region}
                          onChange={(e) =>
                            setData("venue_region", e.target.value)
                          }
                          placeholder={event.region ?? "California"}
                          disabled={!can_edit}
                        />
                      </Field>

                      <Field label="Postcode" error={errors.venue_postal_code}>
                        <Input
                          value={data.venue_postal_code}
                          onChange={(e) =>
                            setData("venue_postal_code", e.target.value)
                          }
                          placeholder="95113"
                          disabled={!can_edit}
                        />
                      </Field>
                    </div>

                    <Field
                      label="Description"
                      hint="Shown on your public event page."
                      error={errors.description}
                    >
                      <Textarea
                        rows={4}
                        value={data.description}
                        onChange={(e) => setData("description", e.target.value)}
                        disabled={!can_edit}
                      />
                    </Field>
                  </>
                )}

                {can_edit && (
                  <div className="flex flex-wrap gap-3">
                    <Button type="submit" disabled={processing}>
                      {processing ? "Saving…" : "Save"}
                    </Button>

                    {can_submit && (
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() =>
                          router.post(`/organize/${event.id}/submit`)
                        }
                      >
                        Submit for review
                      </Button>
                    )}
                  </div>
                )}

                {!can_submit &&
                  event.aasm_state === "draft" &&
                  (event.missing_fields?.length ?? 0) > 0 && (
                    <p className="text-caption text-ink-soft">
                      Before you can submit, fill in:{" "}
                      {event.missing_fields!.join(", ")}.
                    </p>
                  )}
              </div>
            </Panel>
          </form>
        )}
      </div>
    </Layout>
  );
}
